import React, { useState } from "react";
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
  Button,
  Divider,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { buildApiUrl, config } from "../config";
import { brandPalette } from "../theme/brandPalette";
import Footer from "../components/Footer";
import { trackUserRegistered } from "../utils/analytics";

const plans = [
  { value: "pilot", label: "Pilot" },
  { value: "pro", label: "Pro" },
  { value: "enterprise", label: "Enterprise" },
];

// Helper: password validation
const isPasswordValid = (password: string) =>
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(password);

const API_URL = buildApiUrl("");

type FormState = {
  name: string;
  plan: string;
  root_user_name: string;
  root_user_email: string;
  root_user_password: string;
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [form, setForm] = useState<FormState>({
    name: "",
    plan: "",
    root_user_name: "",
    root_user_email: "",
    root_user_password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Basic validation
    if (
      !form.name ||
      !form.plan ||
      !form.root_user_name ||
      !form.root_user_email ||
      !form.root_user_password
    ) {
      setError("All fields are required.");
      return;
    }
    if (!isPasswordValid(form.root_user_password)) {
      setError(
        "Password must be at least 8 characters, include one letter, one number, and one symbol."
      );
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/v1/register-new-tenant`, {
        ...form,
        credit_balance: 0, // hidden; default
        settings: {}, // hidden; default
      });
      // Track successful registration
      trackUserRegistered(form.name, form.plan);
      
      // Redirect directly to login after successful registration
      navigate("/login-pwd", { replace: true });
      return;
    } catch (err: any) {
      let msg =
        err?.response?.data?.detail ||
        "Failed to register. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 560, width: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                Tenant Registered!
              </Typography>
              <Typography>
                <b>Tenant:</b> {success.name}
              </Typography>
              <Typography>
                <b>Root User Email:</b> {success.root_user.email}
              </Typography>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/login-pwd")}
                >
                  Go to Login
                </Button>
              </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        p: { xs: 2, md: 4 },
        pb: 8,
      }}>
        <Box sx={{ maxWidth: 1200, width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ width: { xs: '100%', md: '58%' } }}>
          <Paper elevation={0} sx={{
            p: { xs: 3, md: 5 }, height: '100%', borderRadius: 3,
            bgcolor: '#FFFFFF',
            border: '1px solid', borderColor: 'divider',
            overflow: 'hidden', position: 'relative',
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(94, 77, 178, 0.03) 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}>
            <Box sx={{ position: 'relative' }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
                Welcome to {config.brandName}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 1 }}>
                Business Aware Intelligence Platform
              </Typography>
              <Box sx={{ mt: 4, maxWidth: 520 }}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: brandPalette.neutral[100], borderColor: brandPalette.neutral[300], borderLeft: '4px solid', borderLeftColor: 'primary.main', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    What was our revenue growth in Q2 vs Q1, and how did our profit margins change?
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: brandPalette.neutral[100], borderColor: brandPalette.neutral[300], borderLeft: '4px solid', borderLeftColor: 'primary.main', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' } }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Button size="small" variant="outlined" sx={{ borderColor: brandPalette.neutral[300], color: 'text.primary' as any, fontSize: '0.75rem' }}>📊 Revenue Analysis</Button>
                    <Button size="small" variant="outlined" sx={{ borderColor: brandPalette.neutral[300], color: 'text.primary' as any, fontSize: '0.75rem' }}>💰 Profit Margins</Button>
                    <Button size="small" variant="outlined" sx={{ borderColor: brandPalette.neutral[300], color: 'text.primary' as any, fontSize: '0.75rem' }}>📈 Growth Trends</Button>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Your Q2 revenue grew 23.5% to $3.4M compared to Q1. Profit margins improved from 42% to 47% due to reduced costs and higher average order values.
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ mt: 6 }}>
                <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 800 }}>Ask Questions, Get Answers - Instantly</Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1, maxWidth: 560 }}>
                  Connect your data sources and ask questions in plain English. Get instant, accurate answers with visualizations - no SQL, no waiting, no complexity.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
        <Box sx={{ width: { xs: '100%', md: '42%' } }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Get Started in 60 Seconds
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your account and start asking questions about your data
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <TextField fullWidth margin="normal" label="Company Name" name="name" value={form.name} onChange={handleChange} required />
                <TextField fullWidth select margin="normal" label="Plan" name="plan" value={form.plan} onChange={handleChange} required>
                  {plans.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField fullWidth margin="normal" label="Full Name" name="root_user_name" value={form.root_user_name} onChange={handleChange} required />
                <TextField fullWidth margin="normal" label="Email" name="root_user_email" type="email" value={form.root_user_email} onChange={handleChange} required />
                <TextField
                  fullWidth margin="normal" label="Password" name="root_user_password"
                  type={showPw ? 'text' : 'password'} value={form.root_user_password} onChange={handleChange} required
                  inputProps={{ autoComplete: 'new-password' }} helperText="Min. 8 characters, at least one letter, number, and symbol."
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={() => setShowPw(v => !v)} edge="end">
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ) }}
                />
                <Box mt={1}><LinearProgress variant="determinate" value={Math.min(100, form.root_user_password.length * 10)} aria-label="password strength" /></Box>
                <FormControlLabel control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />} label={
                  <Typography variant="body2">I agree to the <Link href="#">Terms of Service</Link> and <Link href="#">Privacy Policy</Link></Typography>
                } />
                <LoadingButton variant="contained" color="primary" type="submit" fullWidth loading={loading} loadingPosition="start" size="large" sx={{ mt: 1 }}>
                  Sign up with Email
                </LoadingButton>
              </form>
              <Divider sx={{ my: 2 }}>Or continue with</Divider>
              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" fullWidth startIcon={<GoogleIcon />}>Google</Button>
                <Button variant="outlined" fullWidth startIcon={<MicrosoftIcon />}>Microsoft</Button>
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Already have an account?</Typography>
                <Link component="button" type="button" onClick={() => navigate('/login-pwd')}>Sign in</Link>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}
